import React, {useCallback, useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {CheckCircle2, CircleAlert, Loader2} from "lucide-react";
import NavBar from "../components/UserNavBar2";
import {useCart} from "../components/CardContext";
import {useUserLearning} from "../store/user";

const PaymentSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {removeFromCart} = useCart();
  const callbackParams = new URLSearchParams(location.search);
  const reference = callbackParams.get("reference") || callbackParams.get("trxref");
  const {verifyUserPurchase, fetchMyLibrary} = useUserLearning();
  const [status, setStatus] = useState("pending");
  const [message, setMessage] = useState("Verifying your payment with the payment provider…");
  const [transaction, setTransaction] = useState(null);

  const verify = useCallback(async () => {
    if (!reference) {
      setStatus("missing");
      setMessage("Payment reference not found. Open your purchase history to check the transaction.");
      return;
    }
    setStatus("pending");
    setMessage("Verifying your payment with the payment provider…");
    const result = await verifyUserPurchase(reference);
    const data = result.data || {};
    setTransaction(data);
    const paymentStatus = String(data.status || "").toUpperCase();
    if (result.success && paymentStatus === "COMPLETED") {
      setStatus("success");
      setMessage("Your payment is confirmed. Your purchase is available in My Library.");
      if (data.itemId && data.type) {
        removeFromCart(data.itemId, String(data.type).replace(/_PURCHASE$/, "").toLowerCase());
      }
      fetchMyLibrary();
    } else if (result.success && ["PENDING", "PROCESSING"].includes(paymentStatus)) {
      setStatus("pending-result");
      setMessage("Your payment is still being confirmed. You can check again in a moment.");
    } else {
      setStatus("failed");
      setMessage(result.error || `Payment status: ${data.status || "not confirmed"}. You can retry from the marketplace.`);
    }
  }, [reference, verifyUserPurchase, fetchMyLibrary, removeFromCart]);

  useEffect(() => { verify(); }, [verify]);

  return (
    <div className="min-h-screen bg-slate-50">
      <NavBar title="Purchase confirmation" subTitle="Marketplace" />
      <main className="mx-auto flex max-w-3xl justify-center px-4 py-14">
        <section className="w-full rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-10">
          {status === "pending" ? <Loader2 className="mx-auto h-12 w-12 animate-spin text-blue-800" /> : null}
          {status === "success" ? <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /> : null}
          {["failed", "missing", "pending-result"].includes(status) ? <CircleAlert className="mx-auto h-12 w-12 text-amber-600" /> : null}
          <h1 className="mt-4 text-2xl font-bold text-slate-900">
            {status === "success" ? "Purchase successful" : status === "pending" ? "Confirming payment" : status === "pending-result" ? "Payment is processing" : "Payment not confirmed"}
          </h1>
          <p role="status" className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-slate-600">{message}</p>
          {transaction?.reference || reference ? <p className="mt-4 text-xs text-slate-500">Reference: {transaction?.reference || reference}</p> : null}
          {transaction?.amount != null ? <p className="mt-1 text-xs text-slate-500">Amount: ₦{Number(transaction.amount).toLocaleString()}</p> : null}
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            {status === "success" ? <button type="button" onClick={() => navigate("/content-library")} className="rounded-md bg-blue-900 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-800">Go to My Library</button> : null}
            {["failed", "pending-result"].includes(status) ? <button type="button" onClick={verify} className="rounded-md bg-blue-900 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-800">Check payment again</button> : null}
            <button type="button" onClick={() => navigate("/")} className="rounded-md border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">Continue exploring</button>
            <button type="button" onClick={() => navigate("/learner/transactions")} className="rounded-md border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">Purchase history</button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PaymentSuccessPage;
